import React from 'react';
import { Check } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { AssetsConfig } from '@/services/AssetsConfig';

const NotSpecified = () => (
  <span className="text-red-500 font-medium">Not specified</span>
);

const QuestionValue = ({ question, value }) => {
  if (!value) return <NotSpecified />;

  // If we don't have question configuration (old/removed question), treat as text
  if (!question) {
    return <p className="text-gray-700 whitespace-pre-wrap">{value}</p>;
  }
  let selectedOption;
  switch (question.type) {
    case 'multiple_select':
      return (
        <div className="grid grid-cols-2 gap-2">
          {value.map((item) => (
            <div key={item} className="flex items-center gap-2">
              <Check className="h-4 w-4 text-green-500" />
              <span className="text-gray-700">
                {typeof item === 'object' ? item.label : item}
              </span>
            </div>
          ))}
        </div>
      );

    case 'radio':
    case 'dropdown':
      // Handle case where options are objects with value/label
      selectedOption = question.options.find(opt => 
        (typeof opt === 'object' ? opt.value : opt.toLowerCase().replace(/\s+/g, '_')) === value
      );
      return (
        <span className="text-gray-700">
          {selectedOption ? (typeof selectedOption === 'object' ? selectedOption.label : selectedOption) : value}
        </span>
      );

    case 'text':
      return <p className="text-gray-700 whitespace-pre-wrap">{value}</p>;

    case 'file_upload':
      if (!value || value.length === 0) return <NotSpecified />;
      return (
        <div className="space-y-1">
          {value.map((file, index) => (
            <div key={index} className="text-gray-700">{file.name}</div>
          ))}
        </div>
      );

    case 'picture_select':
      selectedOption = question.options.find(opt => opt.value === value);
      if (!selectedOption) return <NotSpecified />;
      return (
        <div className="space-y-2">
          <div className="overflow-hidden rounded-lg border border-gray-200">
            <div className="aspect-video flex items-center justify-center bg-gray-50">
              <img
                src={selectedOption.imageUrl}
                alt={selectedOption.label}
                className="max-w-full max-h-full object-contain"
              />
            </div>
            <div className="p-2 bg-white">
              <p className="text-gray-700">{selectedOption.label}</p>
            </div>
          </div>
        </div>
      );

    default:
      return <span className="text-gray-700">{value}</span>;
  }
};

export const AdditionalInfoContent = ({ details, projectType = 'kitchen' }) => {
  const [questions, setQuestions] = React.useState(null);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    const loadQuestions = async () => {
      try {
        const data = await AssetsConfig.getQuestions({ flow: projectType });
        setQuestions(data);
      } catch (error) {
        console.error('Error loading questions:', error);
      } finally {
        setLoading(false);
      }
    };

    loadQuestions();
  }, [projectType]);

  if (loading) {
    return (
      <div className="flex justify-center py-4">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" />
      </div>
    );
  }

  if (!details) {
    return <p className="text-gray-500">No additional information provided</p>;
  }

  const parsedDetails = typeof details === 'string' ? JSON.parse(details) : details;

  // Get all answer keys that exist in the data
  const answerKeys = Object.keys(parsedDetails).filter(key => 
    parsedDetails[key] !== undefined && 
    parsedDetails[key] !== null && 
    parsedDetails[key] !== ''
  );

  // Create a map of configured questions for easy lookup
  const questionMap = new Map();
  const categoryMap = new Map();
  
  if (questions) {
    questions.categories.forEach(category => {
      category.questions.forEach(question => {
        questionMap.set(question.id, question);
        categoryMap.set(question.id, category);
      });
    });
  }

  // Separate answers into configured and unconfigured
  const configuredAnswers = new Map();
  const unconfiguredAnswers = new Map();

  answerKeys.forEach(key => {
    if (questionMap.has(key)) {
      const category = categoryMap.get(key);
      if (!configuredAnswers.has(category.id)) {
        configuredAnswers.set(category.id, {
          category,
          questions: []
        });
      }
      configuredAnswers.get(category.id).questions.push({
        question: questionMap.get(key),
        value: parsedDetails[key]
      });
    } else {
      unconfiguredAnswers.set(key, parsedDetails[key]);
    }
  });

  return (
    <div className="space-y-8">
      {/* Render configured answers grouped by category */}
      {Array.from(configuredAnswers.values()).map(({ category, questions }) => (
        <div key={category.id} className="space-y-4">
          <h3 className="text-lg font-semibold flex items-center gap-2">
            <span className="text-2xl">{category.icon}</span>
            {category.name}
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {questions.map(({ question, value }) => (
              <div key={question.id} className="space-y-2">
                <p className="text-gray-600 font-medium flex items-center gap-2">
                  {question.icon && <span className="text-xl">{question.icon}</span>}
                  {question.question}
                </p>
                <QuestionValue question={question} value={value} />
              </div>
            ))}
          </div>
        </div>
      ))}

      {/* Render unconfigured answers in a separate section if they exist */}
      {unconfiguredAnswers.size > 0 && (
        <div className="space-y-4">
          <h3 className="text-lg font-semibold flex items-center gap-2">
            <span className="text-2xl">📝</span>
            Additional Details
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {Array.from(unconfiguredAnswers.entries()).map(([key, value]) => (
              <div key={key} className="space-y-2">
                <p className="text-gray-600 font-medium">
                  {key.replace(/([A-Z])/g, ' $1')
                    .replace(/_/g, ' ')
                    .trim()
                    .replace(/\b\w/g, l => l.toUpperCase())}
                </p>
                <QuestionValue question={null} value={value} />
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export const AdditionalInfoSection = ({ projectData, onEdit, isEditable }) => (
  <Card className="mb-4">
    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
      <CardTitle className="text-xl font-bold">Additional Information</CardTitle>
      {isEditable && (
        <Button variant="outline" size="sm" onClick={() => onEdit("needs")}>
          Edit
        </Button>
      )}
    </CardHeader>
    <CardContent>
      <AdditionalInfoContent 
        details={projectData?.kitchenDetails}
        projectType={projectData?.projectType || 'kitchen'} 
      />
    </CardContent>
  </Card>
);

export default AdditionalInfoContent;